import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, CalendarPlus, CheckCircle2, MessageCircle, MessageSquare, Share2, Timer } from "lucide-react";
import { Reveal } from "@/components/motion-primitives";
import { SectionHead, Field, Input, Select, Textarea, usePageMeta } from "@/components/shared";

const PRESETS = {
  "reservation": "Reservation",
  "activity": "Bermuda Activity",
  "dining": "Dining Reservation",
  "transport": "Transport / Transfer",
  "travel": "Travel Reminder",
  "date-night": "Bermuda Date Night",
  "experience": "Bermuda Experience",
  "excursion": "Bermuda Shore Excursion",
  "all-aboard": "All Aboard - Return to Ship",
  "departure": "Leaving Bermuda — Departure",
};

const STANDARD_REMINDERS = [[15, "15 minutes before"], [30, "30 minutes before"], [60, "1 hour before"], [1440, "1 day before"]];
const CRUISE_REMINDERS = [[120, "2 hours before"], [60, "1 hour before"], [30, "30 minutes before"]];

const track = (event, props) => { try { window.posthog?.capture(event, props); } catch { /* analytics optional */ } };

const Countdown = ({ title, date, start }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);
  if (!date || !start) return null;
  const diff = new Date(`${date}T${start}:00`).getTime() - now;
  if (diff <= 0) {
    return (
      <div className="mt-7 bg-navy px-6 py-5 text-center text-ivory" data-testid="cal-countdown">
        <p className="font-serif text-xl">It's time — have a wonderful {title ? "moment" : "time"}.</p>
      </div>
    );
  }
  const mins = Math.floor(diff / 60000);
  const days = Math.floor(mins / 1440);
  const hours = Math.floor((mins % 1440) / 60);
  const minutes = mins % 60;
  return (
    <div className="mt-7 flex items-center justify-between gap-4 bg-navy px-6 py-5 text-ivory" data-testid="cal-countdown">
      <div className="flex items-center gap-3">
        <Timer size={18} strokeWidth={1.5} className="shrink-0 text-bermuda" />
        <p className="text-[11px] uppercase tracking-[0.18em] text-ivory/60">Countdown</p>
      </div>
      <p className="font-serif text-lg leading-snug sm:text-xl" data-testid="cal-countdown-value">
        {days > 0 && <>{days} <span className="text-ivory/55">days</span> · </>}
        {hours} <span className="text-ivory/55">hrs</span> · {minutes} <span className="text-ivory/55">min</span>
      </p>
    </div>
  );
};

const pad2 = (n) => String(n).padStart(2, "0");
const googleDate = (d) => `${d.getUTCFullYear()}${pad2(d.getUTCMonth() + 1)}${pad2(d.getUTCDate())}T${pad2(d.getUTCHours())}${pad2(d.getUTCMinutes())}${pad2(d.getUTCSeconds())}Z`;
const escICS = (s) => String(s || "").replace(/\\/g, "\\\\").replace(/\n/g, "\\n").replace(/,/g, "\\,").replace(/;/g, "\\;");

export default function CalendarBuilder() {
  usePageMeta("My Bermuda Calendar", "Add Bermuda reservations, activities, dining, transport and cruise all-aboard reminders to Google, Apple or Outlook calendar — a free tool from The Bermuda Co-Host.");
  const [params] = useSearchParams();
  const type = params.get("type") || "";
  const isCruise = type === "all-aboard";
  const isExcursion = type === "excursion";
  const reminders = isCruise ? CRUISE_REMINDERS : STANDARD_REMINDERS;

  const [form, setForm] = useState({
    title: PRESETS[type] || params.get("title") || "",
    date: params.get("date") || "",
    start: params.get("start") || "",
    end: params.get("end") || "",
    location: params.get("location") || "",
    confirmation: "", notes: "", reminder: isCruise ? 120 : 60,
    shipName: "", dock: "", cabin: "", meetingPoint: "", operator: "",
  });
  const [error, setError] = useState("");
  const [done, setDone] = useState("");

  useEffect(() => {
    track("calendar_builder_view");
    if (PRESETS[type]) track("preset_type_used");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (k) => (e) => { setForm({ ...form, [k]: e.target.value }); setDone(""); };

  const buildEvent = () => {
    if (!form.date || !form.start) {
      setError("Please choose a date and a start time — everything else is optional.");
      return null;
    }
    setError("");
    const start = new Date(`${form.date}T${form.start}:00`);
    let end = form.end ? new Date(`${form.date}T${form.end}:00`) : new Date(start.getTime() + 60 * 60 * 1000);
    if (end <= start) end = new Date(start.getTime() + 60 * 60 * 1000);
    const detailLines = [];
    if (form.notes.trim()) detailLines.push(form.notes.trim());
    if (form.confirmation.trim()) detailLines.push(`Confirmation: ${form.confirmation.trim()}`);
    if (isCruise) {
      if (form.shipName.trim()) detailLines.push(`Ship: ${form.shipName.trim()}`);
      if (form.dock.trim()) detailLines.push(`Dock / Wharf: ${form.dock.trim()}`);
      if (form.cabin.trim()) detailLines.push(`Cabin: ${form.cabin.trim()}`);
    }
    if (isExcursion) {
      if (form.meetingPoint.trim()) detailLines.push(`Meeting point: ${form.meetingPoint.trim()}`);
      if (form.operator.trim()) detailLines.push(`Tour operator: ${form.operator.trim()}`);
    }
    detailLines.push("Planned with The Bermuda Co-Host — https://thebermudacohost.com");
    return {
      title: form.title.trim() || "Bermuda Plan",
      location: form.location.trim(),
      details: detailLines.join("\n"),
      start, end,
      reminder: Number(form.reminder),
    };
  };

  const openGoogle = () => {
    const e = buildEvent();
    if (!e) return;
    const u = new URL("https://calendar.google.com/calendar/render");
    u.searchParams.set("action", "TEMPLATE");
    u.searchParams.set("text", e.title);
    u.searchParams.set("dates", `${googleDate(e.start)}/${googleDate(e.end)}`);
    u.searchParams.set("details", e.details);
    u.searchParams.set("location", e.location);
    track("google_calendar_click");
    window.open(u.toString(), "_blank", "noopener");
  };

  const openOutlook = () => {
    const e = buildEvent();
    if (!e) return;
    const u = new URL("https://outlook.live.com/calendar/0/deeplink/compose");
    u.searchParams.set("subject", e.title);
    u.searchParams.set("startdt", e.start.toISOString());
    u.searchParams.set("enddt", e.end.toISOString());
    u.searchParams.set("body", e.details);
    u.searchParams.set("location", e.location);
    track("outlook_calendar_click");
    window.open(u.toString(), "_blank", "noopener");
  };

  const downloadICS = () => {
    const e = buildEvent();
    if (!e) return;
    const ics = [
      "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//The Bermuda Co-Host//Calendar Builder//EN",
      "CALSCALE:GREGORIAN", "METHOD:PUBLISH", "BEGIN:VEVENT",
      `UID:${Date.now()}@thebermudacohost.com`, `DTSTAMP:${googleDate(new Date())}`,
      `DTSTART:${googleDate(e.start)}`, `DTEND:${googleDate(e.end)}`,
      `SUMMARY:${escICS(e.title)}`, `LOCATION:${escICS(e.location)}`, `DESCRIPTION:${escICS(e.details)}`,
      "BEGIN:VALARM", "ACTION:DISPLAY", "DESCRIPTION:Reminder", `TRIGGER:-PT${e.reminder}M`, "END:VALARM",
      "END:VEVENT", "END:VCALENDAR",
    ].join("\r\n");
    const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${e.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase() || "bermuda-event"}.ics`;
    a.click();
    URL.revokeObjectURL(a.href);
    track("ics_download");
    setDone(e.title);
    toast.success("Calendar file downloaded — open it to add the event.");
  };

  const shareUrl = () => {
    const u = new URL("https://thebermudacohost.com/calendar");
    if (type) u.searchParams.set("type", type);
    ["title", "date", "start", "end", "location"].forEach((k) => form[k] && u.searchParams.set(k, form[k]));
    return u.toString();
  };

  const shareText = () =>
    `${form.title || "My Bermuda plan"}${form.date ? ` — ${form.date}` : ""}${form.start ? ` at ${form.start}` : ""}${form.location ? `, ${form.location}` : ""}. Planned with The Bermuda Co-Host:`;

  const shareNative = async () => {
    if (!form.date || !form.start) { setError("Add a date and start time before sharing your plan."); return; }
    setError("");
    track("share_plan_click");
    if (navigator.share) {
      try { await navigator.share({ title: form.title || "My Bermuda plan", text: shareText(), url: shareUrl() }); } catch { /* dismissed */ }
    } else {
      try { await navigator.clipboard.writeText(`${shareText()} ${shareUrl()}`); toast.success("Plan copied — paste it anywhere to share."); }
      catch { toast.error("Copy failed — please copy the link manually."); }
    }
  };

  const shareWhatsApp = () => {
    if (!form.date || !form.start) { setError("Add a date and start time before sharing your plan."); return; }
    setError("");
    track("share_plan_click");
    window.open(`https://wa.me/?text=${encodeURIComponent(`${shareText()} ${shareUrl()}`)}`, "_blank", "noopener");
  };

  const shareSms = () => {
    if (!form.date || !form.start) { setError("Add a date and start time before sharing your plan."); return; }
    setError("");
    track("share_plan_click");
    window.open(`sms:?&body=${encodeURIComponent(`${shareText()} ${shareUrl()}`)}`, "_self");
  };

  return (
    <main data-testid="calendar-page">
      <section className="bg-sand-light">
        <div className="mx-auto max-w-[1440px] px-6 pb-16 pt-40 lg:px-10">
          <SectionHead
            overline="Free Guest Tool · From Your Digital Guides"
            title={<>My Bermuda<br /><span className="italic text-bermuda-dark">calendar.</span></>}
            copy="Add a reservation, activity, dinner, transfer or all-aboard reminder to the calendar you already use — Google, Apple or Outlook. No account, nothing stored: it all happens on your device."
            align="center"
          />
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-6 pb-24 lg:px-10">
        <Reveal>
          <div className="mx-auto max-w-4xl border border-navy/10 bg-white p-8 shadow-xl shadow-navy/5 lg:p-12" data-testid="calendar-builder">
            {isCruise && (
              <div className="mb-7 flex items-start gap-3 border border-bermuda/50 bg-pink-soft/50 p-4" data-testid="all-aboard-warning">
                <AlertTriangle size={16} strokeWidth={1.8} className="mt-0.5 shrink-0 text-bermuda-dark" />
                <p className="text-[13px] leading-relaxed text-navy/70">
                  Set this earlier than the ship's final all-aboard time to allow a safe return buffer.
                </p>
              </div>
            )}
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Event / Reservation Name" required>
                <Input data-testid="cal-title" value={form.title} onChange={set("title")} placeholder="e.g. Dinner at The Swizzle Inn" />
              </Field>
              <Field label="Location">
                <Input data-testid="cal-location" value={form.location} onChange={set("location")} placeholder="Optional" />
              </Field>
              <Field label="Date" required>
                <Input data-testid="cal-date" type="date" value={form.date} onChange={set("date")} />
              </Field>
              <Field label="Start Time" required>
                <Input data-testid="cal-start" type="time" value={form.start} onChange={set("start")} />
              </Field>
              <Field label="End Time" hint="Defaults to 1 hour after start">
                <Input data-testid="cal-end" type="time" value={form.end} onChange={set("end")} />
              </Field>
              <Field label="Confirmation #">
                <Input data-testid="cal-confirmation" value={form.confirmation} onChange={set("confirmation")} placeholder="Optional" />
              </Field>
              {isCruise && (
                <>
                  <Field label="Ship Name"><Input data-testid="cal-ship-name" value={form.shipName} onChange={set("shipName")} placeholder="Optional" /></Field>
                  <Field label="Dock / Wharf"><Input data-testid="cal-dock" value={form.dock} onChange={set("dock")} placeholder="e.g. Royal Naval Dockyard" /></Field>
                  <Field label="Cabin Number"><Input data-testid="cal-cabin" value={form.cabin} onChange={set("cabin")} placeholder="Optional" /></Field>
                </>
              )}
              {isExcursion && (
                <>
                  <Field label="Meeting Point"><Input data-testid="cal-meeting-point" value={form.meetingPoint} onChange={set("meetingPoint")} placeholder="Optional" /></Field>
                  <Field label="Tour Operator"><Input data-testid="cal-operator" value={form.operator} onChange={set("operator")} placeholder="Optional" /></Field>
                </>
              )}
              <Field label="Reminder">
                <Select data-testid="cal-reminder" value={form.reminder} onChange={set("reminder")}>
                  {reminders.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                </Select>
              </Field>
            </div>
            <div className="mt-5">
              <Field label="Notes">
                <Textarea data-testid="cal-notes" rows={3} value={form.notes} onChange={set("notes")} placeholder="Anything to remember — what to bring, who you're meeting…" />
              </Field>
            </div>

            {error && <p className="mt-5 border border-bermuda-dark/40 bg-pink-soft/60 px-4 py-3 text-sm text-bermuda-dark" role="alert" data-testid="cal-error">{error}</p>}
            <Countdown title={form.title} date={form.date} start={form.start} />
            {done && (
              <p className="mt-5 flex items-center gap-2.5 border border-sage/50 bg-sage/10 px-4 py-3 text-sm text-navy" data-testid="cal-success">
                <CheckCircle2 size={16} className="shrink-0 text-sage" />
                "{done}" downloaded as a calendar file — open it to add the event with your reminder.
              </p>
            )}

            <div className="mt-8 grid gap-3 sm:grid-cols-2" data-testid="calendar-buttons">
              <button onClick={openGoogle} data-testid="cal-google-btn" className="flex items-center justify-center gap-2.5 bg-navy px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-ivory transition-colors hover:bg-bermuda-dark">
                <CalendarPlus size={15} /> Google Calendar
              </button>
              <button onClick={openOutlook} data-testid="cal-outlook-btn" className="flex items-center justify-center gap-2.5 bg-ocean px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-white transition-colors hover:bg-navy">
                <CalendarPlus size={15} /> Outlook / Microsoft
              </button>
              <button onClick={downloadICS} data-testid="cal-apple-btn" className="flex items-center justify-center gap-2.5 bg-bermuda px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-ivory hover:text-navy">
                <CalendarPlus size={15} /> Apple Calendar (.ics)
              </button>
              <button onClick={downloadICS} data-testid="cal-other-btn" className="flex items-center justify-center gap-2.5 border border-navy/25 px-6 py-4 text-[11px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory">
                <CalendarPlus size={15} /> Other Calendar (.ics)
              </button>
            </div>
            <p className="mt-5 text-xs leading-relaxed text-navy/45">
              Apple Calendar and most calendar apps use the downloaded .ics file — it includes your selected reminder.
              Google and Outlook open a ready-made event in your browser, using your account's reminder settings.
            </p>

            <div className="mt-8 border-t border-navy/10 pt-7" data-testid="share-plan">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-navy/60">Share this plan with your travel companions</p>
              <div className="mt-4 grid gap-3 sm:grid-cols-3">
                <button onClick={shareNative} data-testid="cal-share-btn" className="flex items-center justify-center gap-2.5 border border-navy/25 px-5 py-3.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory">
                  <Share2 size={14} /> Share Plan
                </button>
                <button onClick={shareWhatsApp} data-testid="cal-whatsapp-btn" className="flex items-center justify-center gap-2.5 border border-navy/25 px-5 py-3.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory">
                  <MessageCircle size={14} /> WhatsApp
                </button>
                <button onClick={shareSms} data-testid="cal-sms-btn" className="flex items-center justify-center gap-2.5 border border-navy/25 px-5 py-3.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-navy transition-colors hover:bg-navy hover:text-ivory">
                  <MessageSquare size={14} /> Text Message
                </button>
              </div>
              <p className="mt-3 text-xs text-navy/45">They'll open the same plan, ready to save to their own calendar.</p>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
